/**
 * Pure utility functions for bias graph configuration.
 * Extracted for testability — no Chart.js or DOM dependencies.
 */

import * as dayjs from 'dayjs';

const AXIS_MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

class ChartData {
  constructor (data, times, timeLabel) {
    this.data = data;
    this.times = times;
    this.timeLabel = timeLabel;
  }
}

/**
 *
 */
function getDataLabels (range) {
  let timeLabel;
  let dataLabel;

  switch (range.toUpperCase()) {
    case '1W':
      dataLabel = 'summary_date'; // Note this is different from the other cases.
      timeLabel = 'Bias';
      break;
    case '1M':
      dataLabel = 'record_date';
      timeLabel = 'Bias';
      break;
    case '3M':
      dataLabel = 'record_date'; // @TODO maybe this is what will be used?
      timeLabel = 'Bias';
      break;
    case '1Y':
      dataLabel = 'record_date'; // @TODO maybe this is what will be used?
      timeLabel = 'Bias';
      break;
    case 'ALL':
    default:
      dataLabel = 'record_date'; // @TODO maybe this is what will be used?
      timeLabel = 'Bias';
      break;
  }

  return [
    dataLabel,
    timeLabel
  ];
}

function getAxisDateParts (value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return {
      monthIndex: value.getUTCMonth(),
      day: value.getUTCDate()
    };
  }

  const label = value + '';
  const datePart = label.includes('T') ? label.split('T')[0] : label;
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return undefined;
  }

  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (monthIndex < 0 || monthIndex >= AXIS_MONTH_LABELS.length || day < 1 || day > 31) {
    return undefined;
  }

  return { monthIndex, day };
}

function formatAxisTickDateLabel (value) {
  const dateParts = getAxisDateParts(value);
  if (dateParts !== undefined) {
    return `${AXIS_MONTH_LABELS[dateParts.monthIndex]}-${String(dateParts.day).padStart(2, '0')}`;
  }

  let label = value + '';
  const isIsoDate = label.includes('T');
  if (isIsoDate) {
    label = label.split('T')[0];
  }
  return label.split('-').slice(1).join('-');
}

/**
 *
 */
function convertDataToChartFormat (range, _data = [], debug = false) {
  const [dataLabel, timeLabel] = getDataLabels(range);
  const data = [];
  const times = [];
  const dataDict = {};

  // Skip null/NaN bias_score entries — the API now propagates `null` instead
  // of coercing missing data to `0.0` (see related fix in this PR / #140).
  // Mirrors the filter used in current-bias-boxes/index.js:242.
  const validData = _data.filter(
    event => event.bias_score != null && !isNaN(event.bias_score)
  );

  validData.forEach(event => {
    const time = event[dataLabel];
    if (debug === true && time in dataDict) {
      console.warn('Duplicate time found:', time);
      console.warn('event:', event);
    }
    dataDict[time] = Math.round(event.bias_score * 1000) / 1000;
  });

  Object.keys(dataDict).sort().forEach(key => {
    const val = dataDict[key];
    times.push(key);
    data.push(val);
  });

  return new ChartData(data, times, timeLabel);
}

/**
 *
 */
function createBiasGraphChartOptions (chartData, ymin = -100, ymax = 100, stepSize = 25, optionsOverride) {
  return optionsOverride !== undefined
    ? optionsOverride
    : {
        scales: {
          y: { // Updated to 'y' for version 4
            beginAtZero: true,
            title: {
              display: true,
              text: '   Liberal       Conservative',
              font: {
                size: 12,
                weight: 'bold'
              }
            },
            min: ymin, // Directly under 'y'
            max: ymax, // Directly under 'y'
            ticks: {
              stepSize // Directly under 'ticks'
            }
          },
          x: { // Updated to 'x' for version 4
            title: {
              display: true,
              text: 'DATE',
              /* small size for mobile */
              font: {
                size: 12,
                weight: 'bold'
              }
            },
            ticks: {
              display: true, // Directly under 'x'
              autoskip: true,
              /* eslint-disable-next-line */
              callback: function (value, index) {
                return formatAxisTickDateLabel(chartData.times[index]);
              }
            }
          }
        },
        responsive: true,
        plugins: {
          legend: { // Moved under 'plugins'
            display: false
          },
          title: { // Moved under 'plugins'
            display: false
          },
          tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            callbacks: {
              /* eslint-disable-next-line func-names */
              label: function (context) {
                const value = context.dataset.data[context.dataIndex];
                return [`Bias: ${value}`];
              },
              /* eslint-disable-next-line func-names */
              title: function (context) {
                let date = '' + context[0].label;
                const isTimeStamp = date.includes('T');
                if (isTimeStamp) {
                  if (!date.includes('Z')) {
                    date = date + 'Z';
                  }
                  const time = dayjs(date).format('h:mm A').replace(/^0+/, '');
                  const formattedDate = dayjs(date).format('MMM D, ') + time.toLowerCase();
                  return formattedDate;
                } else {
                  const year = date.split('-')[0];
                  const month = date.split('-')[1];
                  const day = date.split('-')[2];
                  const prettyDate = new Date(year, month - 1, day)
                    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  return prettyDate;
                }
              }
            }
          },
          annotation: {
            annotations: {
              lineAtZero: { // This is the annotation id (can be any string)
                type: 'line',
                yMin: 0, // Start at y=0
                yMax: 0, // End at y=0
                borderColor: 'rgba(0, 0, 0, 0.6)', // Line color
                borderWidth: 2 // Line width
              },
              ...(chartData.data.length > 0
                ? {
                    lastPoint: {
                      type: 'point',
                      xValue: chartData.times[chartData.times.length - 1],
                      yValue: chartData.data[chartData.data.length - 1],
                      radius: 6,
                      backgroundColor: 'rgba(16, 16, 16, 0.8)',
                      borderColor: 'white',
                      borderWidth: 2
                    }
                  }
                : {})
            }
          }
        }
      };
}

/**
 * Returns per-point styling arrays that make the last data point visually distinct.
 */
function buildLastPointStyles (dataLength) {
  return {
    pointRadius: Array.from({ length: dataLength }, (_, i) => i === dataLength - 1 ? 5 : 2),
    pointBackgroundColor: Array.from({ length: dataLength }, (_, i) => i === dataLength - 1 ? 'rgba(16, 16, 16, 0.9)' : 'rgba(16, 16, 16, 0.5)')
  };
}

export { ChartData, getDataLabels, convertDataToChartFormat, createBiasGraphChartOptions, buildLastPointStyles, formatAxisTickDateLabel };
